import React, { useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import TypewriterResponsePlain from '../components/TypeWriterResponsePlain';
import { AuthContext } from '../components/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
const url = new URL(window.location.href);
const finalUrl = `/api/`;

const IMG_API =
  'https://math-image-transcriber-steveszren.replit.app/api/convert';

const NormalButton = ({ type = 'button', onClick, children, ...props }) => {
  return (
    <button type={type} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

const ProblemConditionsItem = ({
  conditionDescription,
  setConditionDescription,
  conditionImage,
  setConditionImage,
  handleConditionImageUpload,
  isProcessingConditionImage,
  setIsProcessingConditionImage,
  setConditionLatex,
}) => {
  const [conditionFile, setConditionFile] = useState(null); // New state to store the File object

  // Save the File object when uploading a file
  const handleFileUpload = (file) => {
    setConditionFile(file);
    handleConditionImageUpload(file);
  };

  return (
    <div>
      <div className="text-gray-700 font-medium mb-4 flex items-center justify-between">
        <div className="flex items-center">
          <i className="fas fa-list-ul mr-2 text-blue-500"></i>
          Problem Conditions
        </div>
      </div>
      <div
        className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300 bg-gradient-to-b from-gray-50 to-white"
        tabIndex={0}
      >
        <div className="w-full mb-4 relative">
          <textarea
            className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
            rows={3}
            placeholder="Enter problem conditions..."
            value={conditionDescription}
            onChange={(e) => setConditionDescription(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-gray-600 mb-2 flex items-center justify-between">
              <span>Add condition image (optional)</span>

              {isProcessingConditionImage && ( // Changed to use the processing state
                <div className="text-xs text-blue-600">
                  <i className="fas fa-sync-alt mr-1"></i>
                  Recognizing image content...
                </div>
              )}
            </div>
            {conditionImage ? (
              <div className="relative group">
                <img
                  src={conditionImage}
                  alt="Condition Image"
                  className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <NormalButton
                    className="!rounded-button bg-white/90 text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-white transition-colors"
                    onClick={() => handleFileUpload(conditionFile)}
                  >
                    <i className="fas fa-sync-alt"></i>
                  </NormalButton>
                  <NormalButton
                    className="!rounded-button bg-red-500/90 text-white w-8 h-8 flex items-center justify-center hover:bg-red-500 transition-colors"
                    onClick={() => {
                      setConditionImage(null);
                      setConditionLatex('');
                      setIsProcessingConditionImage(false);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </NormalButton>
                </div>
              </div>
            ) : (
              <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-all duration-200">
                <div className="text-center">
                  <i className="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-2"></i>
                  <div className="text-sm text-gray-500">
                    Click or drag to upload image
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      const file = e.target.files[0];
                      const reader = new FileReader();
                      reader.onload = () => {
                        setConditionImage(reader.result);
                        setConditionFile(file); // Save the File object
                        handleConditionImageUpload(file); // Pass the File object
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionItem = ({ question, onUpdate, onDelete }) => {
  const questionRef = useRef(null);
  const [questionImage, setQuestionImage] = useState(null);
  const [questionFile, setQuestionFile] = useState(null);

  // Handle focus state
  const handleFocus = () => {
    questionRef.current.classList.add('border-blue-400');
  };

  // Handle blur state
  const handleBlur = () => {
    questionRef.current.classList.remove('border-blue-400');
  };

  // Handle image/text paste
  const handlePaste = (e) => {
    e.preventDefault();
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          handleQuestionImageChange(file); // ✅ Directly pass the File object
        }
      }
    }

    // Process text paste
    const text = e.clipboardData.getData('text');
    if (text) {
      onUpdate({
        ...question,
        description: question.description + text,
      });
    }
  };

  const convertImageToLatex = async (imageData) => {
    try {
      onUpdate({
        ...question,
        isProcessing: true,
      });

      const formData = new FormData();

      formData.append('image', imageData);

      const response = await axios.post(IMG_API, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: false,
      });
      if (response.data.result) {
        onUpdate({
          ...question,
          latex: response.data.result,
          isProcessing: false,
        });
      }
    } catch (err) {
      console.error('Conversion failed:', err);
      onUpdate({
        ...question,
        isProcessing: false, // Reset the state even when an error occurs
      });
    }
  };

  useEffect(() => {
    return () => {
      // Clean up all temporary URLs when the component is unmounted
      if (question.imageURL) {
        URL.revokeObjectURL(question.imageURL);
      }
    };
  }, [question.imageURL]);

  const handleQuestionImageChange = (file) => {
    if (!file) {
      // Clean up resources when deleting the image
      if (question.imageURL) {
        URL.revokeObjectURL(question.imageURL);
      }
      onUpdate({
        ...question,
        image: null,
        imageURL: null, // New field for the temporary URL
        isProcessing: false,
        latex: '',
      });
      return;
    }

    // Generate a temporary preview URL
    const previewURL = URL.createObjectURL(file);

    // Update the state
    onUpdate({
      ...question,
      image: file, // Store the original File object
      imageURL: previewURL, // Store the preview URL
      isProcessing: true,
    });

    // Start the conversion
    convertImageToLatex(file);
  };

  const handleFileUpload = (file) => {
    setQuestionFile(file);
    handleQuestionImageChange(file);
  };

  const handleDeleteImage = () => {
    onUpdate({
      ...question,
      image: null,
    });
  };

  return (
    <div
      ref={questionRef}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPaste={handlePaste}
      className="mb-4 p-6 border border-gray-200 rounded-xl bg-white shadow-sm hover:border-purple-400 hover:shadow-md transition-all duration-200 focus-within:border-purple-400 focus-within:shadow-md focus-within:ring-2 focus-within:ring-purple-100"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-medium text-gray-800">
          Question {question.letter}
        </div>
        <NormalButton
          className="!rounded-button text-red-500 hover:text-red-600 transition-colors duration-200"
          onClick={onDelete}
        >
          <i className="fas fa-trash-alt"></i>
        </NormalButton>
      </div>

      <textarea
        className="w-full mb-4 p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-purple-100 focus:border-purple-400"
        rows={2}
        placeholder={`Enter description for question ${question.letter}...`}
        value={question.description}
        onChange={(e) => onUpdate({ ...question, description: e.target.value })}
      />

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">
              Add Question Image (Optional)
            </div>

            {question.isProcessing && (
              <div className="text-xs text-blue-600">
                <i className="fas fa-sync-alt mr-1"></i>
                Recognizing image content...
              </div>
            )}
          </div>

          {question.image ? (
            <div className="relative group">
              <img
                src={question.image}
                alt={`Question ${question.letter} diagram`}
                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <NormalButton
                  className="!rounded-button bg-white/90 text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-white transition-colors"
                  onClick={() => handleFileUpload(questionFile)}
                >
                  <i className="fas fa-sync-alt"></i>
                </NormalButton>
                <NormalButton
                  className="!rounded-button absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  onClick={() => {
                    setQuestionImage(null);

                    handleDeleteImage();
                  }}
                >
                  <i className="fas fa-times"></i>
                </NormalButton>
              </div>
            </div>
          ) : (
            <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-200">
              <div className="text-center">
                <i className="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-2"></i>
                <div className="text-sm text-gray-500">
                  Click to upload or paste image directly
                </div>
              </div>

              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setQuestionFile(e.target.files?.[0]);
                    const reader = new FileReader();
                    reader.onload = () => {
                      setQuestionImage(reader.result);
                      question.image = reader.result;
                      handleQuestionImageChange(e.target.files[0]);
                    };
                    reader.readAsDataURL(e.target.files[0]);
                  }
                }}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

const SolutionItem = ({
  solution = { content: '', image: null, latex: '' },
  part,
  onUpdate,
  onDelete,
}) => {
  const solutionRef = useRef(null);
  const [solutionImage, setSolutionImage] = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);

  const handleFocus = () => {
    solutionRef.current.classList.add('border-yellow-400');
  };

  const handleBlur = () => {
    solutionRef.current.classList.remove('border-yellow-400');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const items = e.clipboardData.items;

    // Handle image paste
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            onUpdate({
              ...solution,
              image: reader.result,
            });
            console.log('Image data after update:', reader.result); // Output image data
          };
          reader.readAsDataURL(file);
        }
      }
    }

    // Handle text paste
    const text = e.clipboardData.getData('text');
    if (text) {
      onUpdate({
        ...solution,
        content: solution.content + text,
      });
    }
  };

  const handleDeleteImage = () => {
    onUpdate({
      ...solution,
      image: null,
    });
  };

  const handleSolutionImageChange = (file) => {
    if (!file) {
      // Clean up resources when deleting the image
      if (solution.imageURL) {
        URL.revokeObjectURL(solution.imageURL);
      }
      onUpdate({
        ...solution,
        image: null,
        imageURL: null, // New field for temporary URL
        isProcessing: false,
        latex: '',
      });
      return;
    }

    // Generate temporary preview URL
    const previewURL = URL.createObjectURL(file);

    // Update state
    onUpdate({
      ...solution,
      image: file, // Store original File object
      imageURL: previewURL, // Store preview URL
      isProcessing: true,
    });

    // Start conversion
    convertImageToLatex(file);
  };

  const convertImageToLatex = async (imageData) => {
    try {
      onUpdate({
        ...solution,
        isProcessing: true,
      });

      const formData = new FormData();
      formData.append('image', imageData);

      const response = await axios.post(IMG_API, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: false,
      });
      if (response.data.result) {
        onUpdate({
          ...solution,
          latex: response.data.result,
          isProcessing: false,
        });
      }
    } catch (err) {
      console.error('Conversion failed:', err);
      onUpdate({
        ...solution,
        isProcessing: false, // Reset state even when error occurs
      });
    }
  };

  return (
    <div
      ref={solutionRef}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPaste={handlePaste}
      className="mb-4 p-6 border border-gray-200 rounded-xl bg-white shadow-sm hover:border-yellow-400 hover:shadow-md transition-all duration-200 focus-within:border-yellow-400 focus-within:shadow-md focus-within:ring-2 focus-within:ring-yellow-100"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-medium text-gray-800">Part {part}</div>
        <NormalButton
          className="!rounded-button text-red-500 hover:text-red-600"
          onClick={onDelete}
        >
          <i className="fas fa-trash-alt"></i>
        </NormalButton>
      </div>
      <textarea
        className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
        rows={3}
        placeholder="Enter solution content..."
        value={solution.content}
        onChange={(e) => onUpdate({ ...solution, content: e.target.value })}
      />
      {/* Image upload section */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-600">
            Add Solution Image (Optional)
          </div>

          {solution.isProcessing && (
            <div className="text-xs text-blue-600">
              <i className="fas fa-sync-alt mr-1"></i>
              Recognizing image content...
            </div>
          )}
        </div>
        {solution.image ? (
          <div className="relative group">
            <img
              src={solution.image}
              alt="Solution diagram"
              className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <NormalButton
                className="!rounded-button bg-white/90 text-gray-700 w-8 h-8 flex items-center justify-center hover:bg-white transition-colors"
                onClick={() => handleSolutionImageChange(solutionFile)}
              >
                <i className="fas fa-sync-alt"></i>
              </NormalButton>
              <NormalButton
                className="!rounded-button absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleDeleteImage}
              >
                <i className="fas fa-times"></i>
              </NormalButton>
            </div>
          </div>
        ) : (
          <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all">
            <div className="text-center">
              <i className="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-2"></i>
              <div className="text-sm text-gray-500">
                Click to upload or paste image directly
              </div>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  const reader = new FileReader();
                  reader.onload = () => {
                    // onUpdate({ ...solution, image: reader.result });
                    setSolutionFile(e.target.files?.[0]);
                    setSolutionImage(reader.result);
                    solution.image = reader.result;
                    handleSolutionImageChange(e.target.files[0]);
                  };
                  reader.readAsDataURL(e.target.files[0]);
                }
              }}
            />
          </label>
        )}
      </div>
    </div>
  );
};

const AddQuestions = () => {
  const [reviewers, setReviewers] = useState([]);
  const [fileInfo, setFileInfo] = useState(null);
  const [conditionLatex, setConditionLatex] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');
  const [latexCode, setLatexCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [questionTitle, setQuestionTitle] = useState('');
  const [titleImage, setTitleImage] = useState('');

  const [previewImage, setPreviewImage] = useState({ url: '', visible: false });
  const [showTitleImage, setShowTitleImage] = useState(false);

  const [isProcessingConditionImage, setIsProcessingConditionImage] =
    useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [exactMatchCheck, setExactMatchCheck] = useState(false);

  const boardDropdownRef = useRef(null);
  const boardMenuRef = useRef(null);
  const syllabusDropdownRef = useRef(null);
  const syllabusMenuRef = useRef(null);
  const subjectDropdownRef = useRef(null);
  const subjectMenuRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const yearMenuRef = useRef(null);
  const sessionDropdownRef = useRef(null);
  const sessionMenuRef = useRef(null);
  const topicsDropdownRef = useRef(null);
  const topicsMenuRef = useRef(null);

  const { state } = useLocation();
  const isResubmit = state?.isResubmit; // Whether it's a resubmission
  const originalData = state?.originalData; // Original question data

  const version = state?.version;

  // Populate form with original data on initialization
  useEffect(() => {
    if (isResubmit && originalData) {
      setQuestionTitle(originalData.question_title);
      setConditionDescription(originalData.problem_conditions);
      setSelectedBoard(originalData.exam_board);

      // Initialize question image (assuming a question_image field exists)
      if (originalData.question_image) {
        setFileInfo({
          preview: originalData.question_image, // Directly use the URL
          file: null, // Or create a File object as needed
        });
      }

      // Initialize problem condition image
      if (originalData.problem_image) {
        setConditionImage(originalData.problem_image);
        setConditionLatex(originalData.problem_conditions_latex || '');
      }

      // Initialize metadata
      setSelectedSyllabus(originalData.syllabus_code || '');
      setSelectedSubject(originalData.subject || '');
      setSelectedYear(originalData.year_of_exam || '');
      setSelectedSession(originalData.session || '');
      setSelectedLevel(originalData.level || '');
      setSelectedTopicsCovered(originalData.topics_covered || []);
      setExactMatchCheck(originalData.exact_match_check || false);

      // Initialize questions
      const parsedQuestions = originalData.specific_questions; // ✅ Directly use the object
      const initialQuestions = parsedQuestions.map((q, i) => ({
        id: i + 1,
        letter: String.fromCharCode(97 + i),
        description: q.content,
        latex: q.latex,
        image: q.image_url || null,
        imageURL: q.image_url || null,
        isProcessing: false,
      }));
      setQuestions(initialQuestions);

      // Initialize solutions
      const parsedSolutions = originalData.solution_content; // ✅ Directly use the object
      const initialSolutions = parsedSolutions.map((s, i) => ({
        id: i + 1,
        part: String.fromCharCode(97 + i),
        content: s.content,
        latex: s.latex,
        image: s.image_url || null,
        imageURL: s.image_url || null,
        isProcessing: false,
      }));
      setSolutions(initialSolutions);

      // Initialize reviewer (needs to match reviewers list)
      if (originalData.reviewer_id && reviewers.length > 0) {
        const matchedReviewer = reviewers.find(
          (r) => r.id === originalData.reviewer_id,
        );
        setSelectedReviewer(matchedReviewer || null);
      }

      if (originalData.reviewer_id && reviewers?.length > 0) {
        const matchedReviewer = reviewers.find(
          (r) => r.id === originalData.reviewer_id,
        );
        setSelectedReviewer(matchedReviewer || null);
      }
    }
  }, [originalData, reviewers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Exam Board
      const isBoardButton = boardDropdownRef.current?.contains(event.target);
      const isBoardMenu = boardMenuRef.current?.contains(event.target);

      // Syllabus Code
      const isSyllabusButton = syllabusDropdownRef.current?.contains(
        event.target,
      );
      const isSyllabusMenu = syllabusMenuRef.current?.contains(event.target);

      // Subject
      const isSubjectButton = subjectDropdownRef.current?.contains(
        event.target,
      );
      const isSubjectMenu = subjectMenuRef.current?.contains(event.target);

      // Year
      const isYearButton = yearDropdownRef.current?.contains(event.target);
      const isYearMenu = yearMenuRef.current?.contains(event.target);

      // Session
      const isSessionButton = sessionDropdownRef.current?.contains(
        event.target,
      );
      const isSessionMenu = sessionMenuRef.current?.contains(event.target);

      // Topics
      const isTopicsButton = topicsDropdownRef.current?.contains(event.target);
      const isTopicsMenu = topicsMenuRef.current?.contains(event.target);

      if (!isBoardButton && !isBoardMenu) setShowBoardDropdown(false);
      if (!isSyllabusButton && !isSyllabusMenu) setShowSyllabusDropdown(false);
      if (!isSubjectButton && !isSubjectMenu) setShowSubjectDropdown(false);
      if (!isYearButton && !isYearMenu) setShowYearDropdown(false);
      if (!isSessionButton && !isSessionMenu) setShowSessionDropdown(false);
      if (!isTopicsButton && !isTopicsMenu) setShowTopicsDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkAndSaveCustomOption = (type, value, callback) => {
    axios
      .post(finalUrl.concat('save-custom-option'), {
        type,
        value,
      })
      .then((response) => {
        callback(null, response.data);
      })
      .catch((error) => {
        callback(error, null);
      });
  };

  const { user } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    checkAndSaveCustomOption('examBoard', selectedBoard, (err1, result1) => {
      if (err1) {
        console.error('Error saving examBoard:', err1);
        return;
      }
      console.log('examBoard saved result:', result1);

      checkAndSaveCustomOption(
        'syllabusCode',
        selectedSyllabus,
        (err2, result2) => {
          if (err2) {
            console.error('Error saving syllabusCode:', err2);
            return;
          }
          console.log('syllabusCode saved result:', result2);

          checkAndSaveCustomOption(
            'subject',
            selectedSubject,
            (err3, result3) => {
              if (err3) {
                console.error('Error saving subject:', err3);
                return;
              }
              console.log('subject saved result:', result3);

              checkAndSaveCustomOption(
                'yearOfExam',
                selectedYear,
                (err4, result4) => {
                  if (err4) {
                    console.error('Error saving yearOfExam:', err4);
                    return;
                  }
                  console.log('yearOfExam saved result:', result4);

                  checkAndSaveCustomOption(
                    'session',
                    selectedSession,
                    (err5, result5) => {
                      if (err5) {
                        console.error('Error saving session:', err5);
                        return;
                      }
                      console.log('session saved result:', result5);

                      checkAndSaveCustomOption(
                        'level',
                        selectedLevel,
                        (err6, result6) => {
                          if (err6) {
                            console.error('Error saving level:', err6);
                            return;
                          }
                          console.log('level saved result:', result6);

                          checkAndSaveCustomOption(
                            'topicsCovered',
                            selectedTopicsCovered,
                            (err7, result7) => {
                              if (err7) {
                                console.error(
                                  'Error saving topicsCovered:',
                                  err7,
                                );
                                return;
                              }
                              console.log(
                                'topicsCovered saved result:',
                                result7,
                              );
                              console.log('All options saved successfully');
                            },
                          );
                        },
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    });

    try {
      if (isResubmit) {
        // Resubmission mode: call dedicated interface
        await axios.post(`${finalUrl}submit-resubmission`, {
          originalData: originalData, // Original data
          editedData: {
            // User-edited data
            questionTitle,
            specificQuestions: questions,
            solutionContent: solutions,
            submitterId: user.id,
            reviewerId: selectedReviewer.id,
          },
        });
        message.success('Resubmission successful!');
      } else {
        // Construct request data
        const formData = new FormData();
        if (originalData) {
          formData.append('original_id', originalData.id);
          formData.append('version', version + 1);
        }
        formData.append('submitter_id', user.id);
        formData.append('questionTitle', questionTitle);
        if (fileInfo) {
          formData.append('diagram-figure', fileInfo.file); // Assume fileInfo has a file property
        }
        formData.append(
          'problemConditions',
          conditionLatex || conditionDescription,
        );

        formData.append(
          'specificQuestions',
          JSON.stringify(
            questions.map((q) => ({
              letter: q.letter,
              content: encodeURIComponent(q.description),
              latex: encodeURIComponent(q.latex),
            })),
          ),
        );
        formData.append(
          'solutionContent',
          JSON.stringify(
            solutions.map((s) => ({
              part: s.part,
              content: encodeURIComponent(s.content),
              latex: encodeURIComponent(s.latex),
            })),
          ),
        );
        formData.append('examBoard', selectedBoard);
        formData.append('syllabusCode', selectedSyllabus);
        formData.append('subject', selectedSubject);
        formData.append('yearOfExam', selectedYear);
        formData.append('session', selectedSession);
        formData.append('level', selectedLevel);
        formData.append('topicsCovered', JSON.stringify(selectedTopicsCovered));
        formData.append('exactMatchCheck', exactMatchCheck ? 1 : 0);
        formData.append('reviewerId', selectedReviewer?.id || '');

        const response = await axios.post(
          finalUrl.concat('submit-question'),
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        if (response.data.success) {
          // Handle successful submission
          handleClear(); // Clear form
          alert('Question submitted for review successfully!');
        }
      }
    } catch (error) {
      if (error.response) {
        // Handle server errors
        setSubmitError({
          message: error.response.data.message || 'Submission failed',
          errors: error.response.data.errors,
        });
      } else {
        setSubmitError({ message: 'Network error, please check connection' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add state at the top of the AddQuestions component
  const [dropdownData, setDropdownData] = useState({
    examBoards: [],
    syllabusCodes: [],
    subjects: [],
    years: [],
    sessions: [],
    levels: [],
    topics: [],
  });
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true);

  const fetchDropdownData = async () => {
    try {
      const response = await axios.get(finalUrl.concat('dropdowns'));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const data = await fetchDropdownData();
        setDropdownData(data);
      } catch (error) {
        console.error('Failed to load dropdowns:', error);
      } finally {
        setIsLoadingDropdowns(false);
      }
    };

    loadDropdowns();
  }, []);

  // Handle image upload
  const handleConditionImageUpload = async (file) => {
    setIsProcessingConditionImage(true);
    setIsConverting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axios.post(IMG_API, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: false,
      });

      if (response.data.result) {
        setConditionLatex(response.data.result);
      }
    } catch (err) {
      setError(err.message);
      console.error('处理失败:', err);
    } finally {
      setIsConverting(false);
      setIsProcessingConditionImage(false);
    }
  };

  // Modified Render Preview section
  const renderPreviewSection = () => (
    <div className="border rounded-xl p-6 bg-gradient-to-b from-gray-50 to-white shadow-sm">
      <div className="text-gray-800 font-semibold mb-4 flex items-center">
        <i className="fas fa-eye mr-2 text-indigo-500"></i>
        Render Preview
      </div>

      {/* Main problem conditions preview - Use blue color scheme */}
      {conditionLatex && (
        <div className="mb-6 p-4 rounded-lg border-2 border-blue-200 bg-blue-50/30">
          <div className="text-sm font-medium text-blue-600 mb-2 flex items-center">
            <i className="fas fa-list-ul mr-2"></i>
            Problem Conditions
          </div>
          <TypewriterResponsePlain content={conditionLatex} />
        </div>
      )}

      {/* Specific Questions */}
      <div className="space-y-4">
        {questions.map(
          (q) =>
            q.latex && (
              <div
                key={q.id}
                className="p-4 rounded-lg border-2 border-purple-200 bg-purple-50/30"
              >
                <div className="text-sm font-medium text-purple-600 mb-2 flex items-center">
                  <i className="fas fa-question-circle mr-2"></i>
                  Question {q.letter}
                </div>
                <TypewriterResponsePlain content={q.latex} />
              </div>
            ),
        )}
      </div>

      {/* Solution Content */}
      <div className="space-y-4">
        {solutions.map(
          (s) =>
            s.latex && (
              <div
                key={s.id}
                className="p-4 rounded-lg border-2 border-yellow-200 bg-yellow-50/30"
              >
                <div className="text-sm font-medium text-yellow-600 mb-2 flex items-center">
                  <i className="fas fa-lightbulb mr-2"></i>
                  Part {s.part}
                </div>
                <TypewriterResponsePlain content={s.latex} />
              </div>
            ),
        )}
      </div>

      {/* Empty state display */}
      {!conditionLatex &&
        questions.every((q) => !q.latex) &&
        solutions.every((s) => !s.latex) && (
          <div className="bg-white p-8 rounded-lg border border-gray-200 min-h-[200px] flex items-center justify-center shadow-inner">
            <div className="text-gray-400 flex flex-col items-center">
              <i className="fas fa-square-root-alt text-4xl mb-2 text-gray-300"></i>
              {isConverting ? 'Converting...' : 'No formula detected'}
            </div>
          </div>
        )}

      {error && (
        <div className="mt-4 text-red-500 text-sm">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          {error}
        </div>
      )}
    </div>
  );

  const renderReviewerSelector = () => (
    <div className="border rounded-xl p-6 bg-gradient-to-b from-gray-50 to-white shadow-sm mt-4">
      <div className="text-gray-800 font-semibold mb-4 flex items-center">
        <i className="fas fa-user-check mr-2 text-green-500"></i>
        Select a reviewer
      </div>

      <div className="relative">
        <NormalButton
          onClick={() => setShowReviewerDropdown(!showReviewerDropdown)}
          className="w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-blue-400 transition-colors"
        >
          {selectedReviewer ? (
            <div className="flex items-center space-x-3">
              <img
                src={selectedReviewer.avatar}
                alt={selectedReviewer.name}
                className="w-8 h-8 rounded-full object-cover"
              />
              <div>
                <p className="font-medium">{selectedReviewer.name}</p>
                <p className="text-sm text-gray-500">
                  {selectedReviewer.title}
                </p>
              </div>
            </div>
          ) : (
            'Please select a reviewer'
          )}

          <i
            className={`fas fa-chevron-down ${showReviewerDropdown ? 'transform rotate-180' : ''}`}
          />
        </NormalButton>

        {showReviewerDropdown && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            {isLoadingReviewers ? (
              <div className="p-4 text-center text-gray-500">
                <i className="fas fa-spinner fa-spin mr-2"></i>Loading
                reviewers...
              </div>
            ) : (
              reviewers.map((reviewer) => (
                <div
                  key={reviewer.id}
                  onClick={() => {
                    setSelectedReviewer(reviewer);
                    setShowReviewerDropdown(false);
                  }}
                  className="p-3 hover:bg-gray-50 cursor-pointer flex items-center space-x-3 transition-colors"
                >
                  <img
                    src={reviewer.avatar}
                    alt={reviewer.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium">{reviewer.name}</p>
                    <p className="text-sm text-gray-500">{reviewer.title}</p>
                  </div>
                </div>
              ))
            )}

            {!isLoadingReviewers && reviewers.length === 0 && (
              <div className="p-4 text-gray-500">No available reviewers</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const [questions, setQuestions] = useState([
    {
      id: 1,
      letter: 'a',
      description: '',
      image: null,
      imageURL: null,
      latex: '',
      isProcessing: false,
    },
  ]);

  const [solutions, setSolutions] = useState([
    {
      id: 1,
      part: 'a',
      content: '',
      image: null,
      imageURL: null,
      latex: '',
      isProcessing: false,
    },
  ]);

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: questions.length + 1,
        letter: String.fromCharCode(97 + questions.length),
        description: '',
        image: null,
        imageURL: null,
        latex: '',
        isProcessing: false,
      },
    ]);
  };

  const handleAddSolution = () => {
    setSolutions([
      ...solutions,
      {
        id: solutions.length + 1,
        part: String.fromCharCode(97 + solutions.length),
        content: '',
        image: null,
      },
    ]);
  };

  const [conditionDescription, setConditionDescription] = useState('');
  const [conditionImage, setConditionImage] = useState(null);

  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [showReviewerDropdown, setShowReviewerDropdown] = useState(false);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedSyllabus, setSelectedSyllabus] = useState('');
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const [showSyllabusDropdown, setShowSyllabusDropdown] = useState(false);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('');
  const levelDropdownRef = useRef(null);
  const levelMenuRef = useRef(null);
  const [showTopicsDropdown, setShowTopicsDropdown] = useState(false);
  const [selectedTopicsCovered, setSelectedTopicsCovered] = useState('');
  const dropZoneRef = useRef(null);

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     const isLevelButton = levelDropdownRef.current?.contains(event.target);
  //     const isLevelMenu = levelMenuRef.current?.contains(event.target);

  //     if (!isLevelButton && !isLevelMenu) {
  //       setShowLevelDropdown(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, []);

  useEffect(() => {
    const fetchReviewers = async () => {
      setIsLoadingReviewers(true);
      try {
        const response = await axios.get(finalUrl.concat('reviewers'));
        if (response.data.success) {
          setReviewers(response.data.data);
        }
      } catch (error) {
        console.error('Failed to get reviewers:', error);
      } finally {
        setIsLoadingReviewers(false);
      }
    };

    fetchReviewers();
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };
  const handleFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setFileInfo({
          file,
          preview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const simulateLatexConversion = () => {
    setIsLoading(true);
    setTimeout(() => {
      setLatexCode(`\\begin{align*}
& \\text{Given: In right - angled triangle ABC, } \\angle C = 90^{\\circ} \\\\
& \\text{Hypotenuse } AB = c \\text{, the two right - angled sides } BC = a \\text{, } AC = b \\\\
& \\text{Then: } c^2 = a^2 + b^2 \\text{ (Pythagorean theorem)} \\\\
& \\text{Satisfies: } \\sin A = \\frac{a}{c}, \\cos A = \\frac{b}{c}, \\tan A = \\frac{a}{b}
\\end{align*}`);
      setIsLoading(false);
    }, 1500);
  };
  const handleClear = () => {
    setFileInfo(null); // Diagrams and Figures
    // Clear the problem condition image
    setConditionImage(null);
    setConditionLatex('');
    // Clear all question images
    setQuestions((prev) =>
      prev.map((q) => ({
        ...q,
        image: null,
        imageURL: null,
        latex: '',
        isProcessing: false,
      })),
    );

    // Clear all solution images
    setSolutions((prev) =>
      prev.map((s) => ({
        ...s,
        image: null,
        imageURL: null,
        latex: '',
        isProcessing: false,
      })),
    );
    // Clear the title image
    setTitleImage('');

    // Clear the preview image
    setPreviewImage({ url: '', visible: false });

    // Release all object URL resources
    questions.forEach((q) => {
      if (q.imageURL) URL.revokeObjectURL(q.imageURL);
    });
    solutions.forEach((s) => {
      if (s.imageURL) URL.revokeObjectURL(s.imageURL);
    });

    // Reset other related states
    setQuestionTitle('');
    setConditionDescription('');
    setLatexCode('');
    setError('');
    document.querySelectorAll('input[type="file"]').forEach((input) => {
      input.value = null;
    });
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(latexCode);
  };

  const handleSolutionPaste = (e, questionIndex) => {
    e.preventDefault();
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            const newQuestions = [...questions];
            newQuestions[questionIndex].solution.image = reader.result;
            setQuestions(newQuestions);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const [isTitleProcessing, setIsTitleProcessing] = useState(false);

  const handleTitleImageUpload = async (file) => {
    setIsTitleProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      // Call the OCR API
      const response = await axios.post(IMG_API, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: false,
      });

      // Fill the recognition result into the title
      if (response.data.result) {
        setQuestionTitle(response.data.result);
      }
    } catch (err) {
      console.error('OCR recognition failed:', err);
      setQuestionTitle('Recognition failed. Please enter manually.');
    } finally {
      setIsTitleProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* 所有现有表单内容 */}
        {/* Main content area */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center mb-8"></div>
          {/* Action buttons area */}
          <div className="mb-8 flex gap-4 justify-center">
            <NormalButton
              className="!rounded-button bg-white border border-gray-200 text-gray-700 px-6 py-3 flex items-center hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-sm whitespace-nowrap"
              onClick={handleClear}
            >
              <i className="fas fa-trash-alt mr-2 text-gray-500"></i>
              Clear Content
            </NormalButton>
          </div>
          {/* Two-column content area */}
          <div className="grid grid-cols-2 gap-8 bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {/* Left upload area */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="text-gray-700 font-medium mb-2 flex items-center">
                    <i className="fas fa-heading mr-2 text-blue-500"></i>
                    Question Title
                  </div>
                  <div className="flex w-full gap-2">
                    <div className="relative flex-1">
                      <div className="flex items-center gap-2">
                        {titleImage && (
                          <div className="relative w-10 h-10 rounded-lg border border-gray-200 cursor-pointer hover:border-blue-400 transition-all duration-200">
                            <img
                              src={titleImage}
                              alt="Title Image"
                              className="w-full h-full object-cover rounded-lg"
                              onClick={() => setShowTitleImage(true)}
                            />
                            <NormalButton
                              className="absolute -right-2 -top-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center 
                   shadow-md hover:bg-red-600 transition-colors z-10 ring-2 ring-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTitleImage('');
                                URL.revokeObjectURL(titleImage);
                              }}
                            >
                              {/* Custom × icon */}
                              <div className="relative w-3 h-3">
                                <div className="absolute w-full h-px bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45"></div>
                                <div className="absolute w-full h-px bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-45"></div>
                              </div>
                            </NormalButton>
                            {isTitleProcessing && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <i className="fas fa-sync-alt animate-spin text-white"></i>
                              </div>
                            )}
                          </div>
                        )}
                        <input
                          type="text"
                          placeholder={
                            isTitleProcessing
                              ? 'Processing...'
                              : 'Enter question title or paste image'
                          }
                          value={questionTitle}
                          onChange={(e) => setQuestionTitle(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const items = e.clipboardData.items;
                            for (let i = 0; i < items.length; i++) {
                              if (items[i].type.indexOf('image') !== -1) {
                                const file = items[i].getAsFile();
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    setTitleImage(reader.result);

                                    handleTitleImageUpload(file);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all duration-200 text-gray-700 placeholder:text-gray-400"
                        />
                      </div>
                      <label className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer hover:opacity-80 transition-opacity">
                        <i className="fas fa-image text-gray-400"></i>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            if (e.target.files?.[0]) {
                              const file = e.target.files[0];
                              const reader = new FileReader();
                              reader.onload = () => {
                                setTitleImage(reader.result);
                                handleTitleImageUpload(file); // Call OCR processing
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                {/* Image preview modal */}
                {(showTitleImage || previewImage.visible) && (
                  <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => {
                      setShowTitleImage(false);
                      setPreviewImage({ ...previewImage, visible: false });
                    }}
                  >
                    <div
                      className="relative bg-white rounded-lg p-4 max-w-3xl max-h-[90vh] overflow-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <NormalButton
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 !rounded-button"
                        onClick={() => {
                          setShowTitleImage(false);
                          setPreviewImage({ ...previewImage, visible: false });
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </NormalButton>
                      <img
                        src={showTitleImage ? titleImage : previewImage.url}
                        alt="Preview Image"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}
                {/* Delete/save title buttons */}
              </div>
              {/* Question images area */}
              <div className="space-y-2 mb-4">
                <div className="text-gray-700 font-medium mb-2 flex items-center">
                  <i className="fas fa-image mr-2 text-blue-500"></i>
                  Diagrams and Figures
                </div>
                <div
                  tabIndex={0}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-blue-500 focus-within:border-blue-500 focus-within:bg-blue-50/30 hover:bg-blue-50/30 active:bg-blue-100/40 transition-all duration-300 bg-gradient-to-b from-gray-50 to-white"
                  onPaste={(e) => {
                    e.preventDefault();
                    const items = e.clipboardData.items;
                    for (let i = 0; i < items.length; i++) {
                      if (items[i].type.indexOf('image') !== -1) {
                        const file = items[i].getAsFile();
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => {
                            setFileInfo({
                              file: file,
                              preview: reader.result,
                            });
                          };
                          reader.readAsDataURL(file);
                        }
                      }
                    }
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="text-sm text-gray-600 mb-2">
                        Add diagrams and figures (Optional)
                      </div>
                      {fileInfo ? (
                        <div className="relative group">
                          <img
                            src={fileInfo.preview}
                            alt="Question Image"
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 cursor-pointer transform hover:scale-[1.02] transition-transform duration-200"
                            onClick={() =>
                              setPreviewImage({
                                url: fileInfo.preview,
                                visible: true,
                              })
                            }
                          />
                          <div className="absolute -top-2 -right-2">
                            <NormalButton
                              className="!rounded-button bg-red-500/90 text-white w-6 h-6 flex items-center justify-center hover:bg-red-500 transition-colors shadow-md"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFileInfo(null);
                              }}
                            >
                              <i className="fas fa-times text-sm"></i>
                            </NormalButton>
                          </div>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-200">
                          <div className="text-center">
                            <i className="fas fa-cloud-upload-alt text-2xl text-gray-400 mb-2"></i>
                            <div className="text-sm text-gray-500">
                              Click or drag to upload image
                            </div>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  setFileInfo({
                                    file: e.target.files[0],
                                    preview: reader.result,
                                  });
                                };
                                reader.readAsDataURL(e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4 mb-4">
                {/* Problem conditions description */}
                <ProblemConditionsItem
                  conditionDescription={conditionDescription}
                  setConditionDescription={setConditionDescription}
                  conditionImage={conditionImage}
                  setConditionImage={setConditionImage}
                  handleConditionImageUpload={handleConditionImageUpload}
                  isProcessingConditionImage={isProcessingConditionImage}
                  setIsProcessingConditionImage={setIsProcessingConditionImage}
                  setConditionLatex={setConditionLatex}
                  isConverting={isConverting}
                  error={error}
                />
                {/* Specific questions list */}
                <div>
                  <div className="text-gray-700 font-medium mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-tasks mr-2 text-purple-500"></i>
                      Specific Questions
                    </div>
                    <NormalButton
                      className="!rounded-button px-4 py-2 bg-purple-500 text-white hover:bg-purple-600 transition-colors duration-200 text-sm flex items-center whitespace-nowrap"
                      onClick={handleAddQuestion}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Question
                    </NormalButton>
                  </div>

                  {questions.map((question) => (
                    <div key={question.id}>
                      <QuestionItem
                        key={question.id}
                        question={question}
                        onUpdate={(updatedQuestion) => {
                          const newQuestions = questions.map((q) =>
                            q.id === updatedQuestion.id ? updatedQuestion : q,
                          );
                          setQuestions(newQuestions);
                        }}
                        onDelete={() => {
                          const newQuestions = questions
                            .filter((q) => q.id !== question.id)
                            .map((q, idx) => ({
                              ...q,
                              letter: String.fromCharCode(97 + idx),
                              image: null, // ✅ Clear image data
                              imageURL: null, // ✅ Clear preview URL
                            }));
                          setQuestions(newQuestions);
                        }}
                      />
                    </div>
                  ))}
                </div>
                {/* Solution content area */}
                <div className="mt-8">
                  <div className="text-gray-700 font-medium mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
                      Solution Content
                    </div>
                    <NormalButton
                      className="!rounded-button px-4 py-2 bg-yellow-500 text-white hover:bg-yellow-600 transition-colors duration-200 text-sm flex items-center whitespace-nowrap"
                      onClick={handleAddSolution}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Solution
                    </NormalButton>
                  </div>

                  {solutions.map((solution) => (
                    <SolutionItem
                      key={solution.id}
                      part={solution.part}
                      solution={solution}
                      onUpdate={(updatedSolution) => {
                        const newSolutions = solutions.map((s) =>
                          s.id === updatedSolution.id ? updatedSolution : s,
                        );
                        setSolutions(newSolutions);
                      }}
                      onDelete={() => {
                        const newSolutions = solutions.filter(
                          (s) => s.id !== solution.id,
                        );
                        setSolutions(
                          newSolutions.map((s, idx) => ({
                            ...s,
                            part: String.fromCharCode(97 + idx),
                          })),
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            {/* Right conversion results area */}
            <div className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
                    <i className="fas fa-circle-notch fa-spin text-5xl text-blue-500 mb-4"></i>
                    <div className="text-gray-700 font-medium">
                      Converting...
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      Please wait a moment.
                    </div>
                    <div className="mt-4 w-32 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden">
                      <div className="h-full bg-blue-500 animate-pulse rounded-full"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {renderPreviewSection()}
                  {/* Render results area */}

                  {/* Exam Board and Syllabus Selection */}

                  <div className="border rounded-xl p-6 bg-gradient-to-b from-gray-50 to-white shadow-sm mb-4">
                    <div className="space-y-4">
                      {/* Exam Board */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between">
                        <div className="flex items-center">
                          <i className="fas fa-university mr-2 text-blue-500"></i>
                          Exam Board
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={boardDropdownRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedBoard}
                              onChange={(e) => setSelectedBoard(e.target.value)}
                              placeholder="Select or type..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowBoardDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showBoardDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowBoardDropdown(!showBoardDropdown)
                              }
                            ></i>
                          </div>

                          {showBoardDropdown && (
                            <div
                              className="absolute top-full right-0 left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10"
                              ref={boardMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading...
                                </div>
                              ) : (
                                dropdownData.examBoards.map((board) => (
                                  <div
                                    key={board.value}
                                    className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                    onClick={() => {
                                      setSelectedBoard(board.value);
                                      setShowBoardDropdown(false);
                                    }}
                                  >
                                    {board.value}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Syllabus Code */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-book mr-2 text-blue-500"></i>
                          Syllabus Code
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={syllabusDropdownRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedSyllabus}
                              onChange={(e) =>
                                setSelectedSyllabus(e.target.value)
                              }
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowSyllabusDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showSyllabusDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowSyllabusDropdown(!showSyllabusDropdown)
                              }
                            ></i>
                          </div>
                          {showSyllabusDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                              ref={syllabusMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading...
                                </div>
                              ) : (
                                <>
                                  {/* Dynamically render API data */}
                                  {dropdownData.syllabusCodes?.map(
                                    (syllabus) => (
                                      <div
                                        key={syllabus.value}
                                        className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                        onClick={() => {
                                          setSelectedSyllabus(syllabus.value);
                                          setShowSyllabusDropdown(false);
                                        }}
                                      >
                                        {syllabus.value}
                                      </div>
                                    ),
                                  )}
                                  {/* Empty state handling */}
                                  {!isLoadingDropdowns &&
                                    dropdownData.syllabusCodes?.length ===
                                      0 && (
                                      <div className="px-4 py-2.5 text-gray-500 text-sm">
                                        No syllabus codes available
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-graduation-cap mr-2 text-blue-500"></i>
                          Subject
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={subjectDropdownRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedSubject}
                              onChange={(e) =>
                                setSelectedSubject(e.target.value)
                              }
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowSubjectDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showSubjectDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowSubjectDropdown(!showSubjectDropdown)
                              }
                            ></i>
                          </div>
                          {showSubjectDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                              ref={subjectMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading subjects...
                                </div>
                              ) : (
                                <>
                                  {dropdownData.subjects?.map((subject) => (
                                    <div
                                      key={subject.value}
                                      className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                      onClick={() => {
                                        setSelectedSubject(subject.value);
                                        setShowSubjectDropdown(false);
                                      }}
                                    >
                                      {subject.value}
                                    </div>
                                  ))}
                                  {!isLoadingDropdowns &&
                                    dropdownData.subjects?.length === 0 && (
                                      <div className="px-4 py-2.5 text-gray-500 text-sm">
                                        No subjects available
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Year of Exam */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-calendar mr-2 text-blue-500"></i>
                          Year of Exam
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={yearDropdownRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(e.target.value)}
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowYearDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showYearDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowYearDropdown(!showYearDropdown)
                              }
                            ></i>
                          </div>

                          {showYearDropdown && (
                            <div
                              className="absolute top-full right-0 left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10"
                              ref={yearMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading...
                                </div>
                              ) : (
                                dropdownData.years
                                  .sort((a, b) => b.value - a.value) // Ensure descending order
                                  .map((year) => (
                                    <div
                                      key={year.value}
                                      className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                      onClick={() => {
                                        setSelectedYear(year.value);
                                        setShowYearDropdown(false);
                                      }}
                                    >
                                      {year.value}
                                    </div>
                                  ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Session */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-clock mr-2 text-blue-500"></i>
                          Session
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={sessionDropdownRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedSession}
                              onChange={(e) =>
                                setSelectedSession(e.target.value)
                              }
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowSessionDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showSessionDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowSessionDropdown(!showSessionDropdown)
                              }
                            ></i>
                          </div>

                          {showSessionDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                              ref={sessionMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading sessions...
                                </div>
                              ) : (
                                <>
                                  {dropdownData.sessions?.map((session) => (
                                    <div
                                      key={session.value}
                                      className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                      onClick={() => {
                                        setSelectedSession(session.value);
                                        setShowSessionDropdown(false);
                                      }}
                                    >
                                      {session.value}
                                    </div>
                                  ))}
                                  {!isLoadingDropdowns &&
                                    dropdownData.sessions?.length === 0 && (
                                      <div className="px-4 py-2.5 text-gray-500 text-sm">
                                        No sessions available
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Level - Displayed on new line */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-layer-group mr-2 text-blue-500"></i>
                          Level
                        </div>
                        <div className="relative inline-flex flex-1 ml-4">
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedLevel}
                              onChange={(e) => setSelectedLevel(e.target.value)}
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowLevelDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showLevelDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowLevelDropdown(!showLevelDropdown)
                              }
                            ></i>
                          </div>

                          {showLevelDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                              ref={levelMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading levels...
                                </div>
                              ) : (
                                <>
                                  {dropdownData.levels?.map((level) => (
                                    <div
                                      key={level.value}
                                      className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                      onClick={() => {
                                        setSelectedLevel(level.value);
                                        setShowLevelDropdown(false);
                                      }}
                                    >
                                      {level.value}
                                    </div>
                                  ))}
                                  {!isLoadingDropdowns &&
                                    dropdownData.levels?.length === 0 && (
                                      <div className="px-4 py-2.5 text-gray-500 text-sm">
                                        No levels available
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Topics Covered */}
                      <div className="text-gray-800 font-semibold flex items-center justify-between mt-4">
                        <div className="flex items-center">
                          <i className="fas fa-bookmark mr-2 text-blue-500"></i>
                          Topics Covered
                        </div>
                        <div
                          className="relative inline-flex flex-1 ml-4"
                          ref={topicsMenuRef}
                        >
                          <div className="w-full relative">
                            <input
                              type="text"
                              value={selectedTopicsCovered}
                              onChange={(e) =>
                                setSelectedTopicsCovered(e.target.value)
                              }
                              placeholder="Select or type ..."
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 pr-8"
                              onClick={() => setShowTopicsDropdown(true)}
                            />
                            <i
                              className={`fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer ${
                                showTopicsDropdown ? 'rotate-180' : ''
                              }`}
                              onClick={() =>
                                setShowTopicsDropdown(!showTopicsDropdown)
                              }
                            ></i>
                          </div>

                          {showTopicsDropdown && (
                            <div
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                              ref={topicsMenuRef}
                            >
                              {isLoadingDropdowns ? (
                                <div className="p-2 text-center text-gray-500">
                                  <i className="fas fa-spinner fa-spin mr-2"></i>
                                  Loading levels...
                                </div>
                              ) : (
                                <>
                                  {dropdownData.topics?.map((topic) => (
                                    <div
                                      key={topic.value}
                                      className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-gray-700 text-sm transition-colors duration-200"
                                      onClick={() => {
                                        setSelectedTopicsCovered(topic.value);
                                        setShowTopicsDropdown(false);
                                      }}
                                    >
                                      {topic.value}
                                    </div>
                                  ))}
                                  {!isLoadingDropdowns &&
                                    dropdownData.topics?.length === 0 && (
                                      <div className="px-4 py-2.5 text-gray-500 text-sm">
                                        No topics available
                                      </div>
                                    )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LaTeX code area */}
                  <div className="border rounded-xl p-6 bg-gradient-to-b from-gray-50 to-white shadow-sm">
                    {/* Similarity check area */}
                    <div className="pt-6">
                      {/* Modified Exact Match Check section */}
                      <div className="border rounded-xl p-6 bg-gradient-to-b from-gray-50 to-white shadow-sm">
                        <div className="text-gray-800 font-semibold mb-4 flex items-center">
                          <i className="fas fa-check-double mr-2 text-green-500"></i>
                          Exact Match Check
                        </div>

                        <div className="flex gap-4">
                          <NormalButton
                            onClick={() => setExactMatchCheck(0)}
                            className={`!rounded-button flex-1 py-3 border transition-all duration-200 group whitespace-nowrap
          ${
            exactMatchCheck === 0
              ? 'border-green-500 bg-green-50'
              : 'border-gray-200 hover:border-green-500 hover:bg-green-50'
          }`}
                          >
                            <i
                              className={`fas fa-check mr-2 ${exactMatchCheck === 0 ? 'text-green-600' : 'text-green-500'}`}
                            ></i>
                            <span
                              className={`${exactMatchCheck === 0 ? 'text-green-600' : 'text-gray-600 group-hover:text-green-600'}`}
                            >
                              No Difference
                            </span>
                          </NormalButton>

                          <NormalButton
                            onClick={() => setExactMatchCheck(1)}
                            className={`!rounded-button flex-1 py-3 border transition-all duration-200 group whitespace-nowrap
          ${
            exactMatchCheck === 1
              ? 'border-red-500 bg-red-50'
              : 'border-gray-200 hover:border-red-500 hover:bg-red-50'
          }`}
                          >
                            <i
                              className={`fas fa-times mr-2 ${exactMatchCheck === 1 ? 'text-red-600' : 'text-red-500'}`}
                            ></i>
                            <span
                              className={`${exactMatchCheck === 1 ? 'text-red-600' : 'text-gray-600 group-hover:text-red-600'}`}
                            >
                              Has Difference
                            </span>
                          </NormalButton>
                        </div>
                      </div>

                      {/* Modified submit button */}
                      <div className="mt-8 space-y-4">
                        {renderReviewerSelector()}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className={`!rounded-button w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 
              hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md whitespace-nowrap
              ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane mr-2"></i>
                              Submit for Review
                            </>
                          )}
                        </button>
                      </div>
                      {/* Add error display */}
                      {submitError && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <h3 className="text-red-600 font-medium">
                            Submission Error:
                          </h3>
                          <p className="text-red-500">{submitError.message}</p>
                          {submitError.errors && (
                            <ul className="mt-2 list-disc pl-5">
                              {submitError.errors.map((err, index) => (
                                <li
                                  key={index}
                                  className="text-red-500 text-sm"
                                >
                                  {err.field}: {err.message}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
export default AddQuestions;
